/* eslint-disable */
import CssBaseline from '@material-ui/core/CssBaseline';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import isEqual from 'lodash/isEqual';
import MuiPickersUtilsProvider from 'material-ui-pickers/MuiPickersUtilsProvider';
import MomentUtils from 'material-ui-pickers/utils/moment-utils';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import themeOptionsSelector from '../../State/Store/Theme/Selectors/ThemeOptions.selector';
import Jss from '../Jss/Jss.component';
import ThemePropTypes from './Theme.component.props';

const doc = document;

/**
 * ThemeComponent
 * @export
 * @class ThemeComponent
 * @extends {React.Component<ThemeComponentComposedProps, ThemeComponentState>}
 */
export class ThemeComponent extends React.Component {
  /**
   * The `props` to seed when any specific prop receives an `undefined` value. **NOT** a `null` value
   * @static
   * @type {Partial<ThemeComponentProps>}
   * @memberof ThemeComponent
   */
  static defaultProps = {};

  /**
   * The name of the Component to display when debugging
   * @static
   * @type {`Theme`}
   * @memberof ThemeComponent
   */
  static displayName = `Theme`;

  /**
   * The values to use to apply runtime typechecking on the props
   * @static
   * @type {object}
   * @memberof ThemeComponent
   */
  static propTypes = ThemePropTypes;

  /**
   * The current theme to render
   * @type {MuiTheme}
   * @memberof ThemeComponent
   */
  _theme;

  /**
   * Data specific to the `<ThemeComponent />` instance
   * @type {ThemeComponentState}
   * @memberof ThemeComponent
   */
  state = {};

  /**
   * If `this.props.themeOptions` ever changes, recreate the `theme`
   * @param {React.props} prevProps The `props` prior to the update
   * @returns {void} void
   * @memberof ThemeComponent
   */
  componentDidUpdate(prevProps) {
    const { themeOptions } = this.props;
    if (!isEqual(themeOptions, prevProps.themeOptions)) {
      this._theme = this.createTheme();
      this.forceUpdate();
    }
  }

  /**
   * Create a theme to be used by our app
   * @returns {MuiTheme} The created theme
   * @memberof ThemeComponent
   */
  createTheme = () => {
    const { themeOptions } = this.props;
    // we need to check if spacing.unit exists. This was the case with materialUI v3 sow when we updated to V4 it expects spacing as a number, so "spacing: 8" not "spacing: { unit: 8}". We saving theme in local storage and so when app open the old version (with "spacing: { unit: 8 }") will be pulled out and as updated app expects "spacing: 8" it will cause layout issues. So for whoever has old version in local storage we modifying it to new structure
    const theme = createMuiTheme({...themeOptions, spacing: (themeOptions.spacing && themeOptions.spacing.unit ? 8 : themeOptions.spacing)});
    this.updateBodyBackgroundColor(theme.palette.background.default);
    return theme;
  };

  /**
   * Update the background-color of `body`
   * @param {string} color The color to update to
   * @returns {void} void
   * @memberof ThemeComponent
   */
  updateBodyBackgroundColor = (color) => {
    doc.body.style.backgroundColor = color;
  };

  /**
   * Render the theme provider when we have a valid theme to use
   * @returns {React.Component | null} The rendered `<ThemeComponent />`
   * @memberof ThemeComponent
   */
  renderThemeProvider = () => {
    if (!this._theme) {
      this._theme = this.createTheme();
    }
    const { children } = this.props;
    return (
      <MuiThemeProvider theme={this._theme}>
        <MuiPickersUtilsProvider utils={MomentUtils}>{children}</MuiPickersUtilsProvider>
      </MuiThemeProvider>
    );
  };

  /**
   * Render the component
   * @returns {React.Component} The rendered `<ThemeComponent />`
   * @memberof ThemeComponent
   */
  render() {
    return (
      <Jss>
        <React.Fragment>
          <CssBaseline />
          {this.renderThemeProvider()}
        </React.Fragment>
      </Jss>
    );
  }
}

/** @type {React.ComponentClass<ThemeComponentProps>} */
const Theme = compose(
  connect((state) => ({
    themeOptions: themeOptionsSelector(state),
  })),
)(ThemeComponent);

export default Theme;
